package com.milta.feature.settings.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import com.milta.feature.subscription.domain.usecase.GetMySubscriptionUseCase
import com.milta.feature.subscription.domain.usecase.GetSubscriptionPlansUseCase
import com.milta.feature.team.domain.usecase.DeleteEnterpriseUseCase
import com.milta.feature.team.domain.usecase.GetMyEnterpriseUseCase
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

/**
 * ViewModel for the web dashboard's "Paramètres" screen (MIL-53). Reuses the exact same use cases
 * as mobile/other web screens — nothing new is invented for profile/language/subscription, only
 * [GetMyEnterpriseUseCase]/[DeleteEnterpriseUseCase] are new (backend had no read/delete endpoint
 * for the Organisation itself before this ticket).
 */
class WebSettingsViewModel(
    private val getProfile: GetProfileUseCase,
    private val updateLanguage: UpdateLanguageUseCase,
    private val getMyEnterprise: GetMyEnterpriseUseCase,
    private val getMySubscription: GetMySubscriptionUseCase,
    private val getSubscriptionPlans: GetSubscriptionPlansUseCase,
    private val deleteEnterprise: DeleteEnterpriseUseCase,
) : MviViewModel<WebSettingsState, WebSettingsIntent>(WebSettingsState()) {

    private val _navEvent = Channel<WebSettingsNavEvent>(Channel.BUFFERED)
    val navEvent = _navEvent.receiveAsFlow()

    init {
        onIntent(WebSettingsIntent.Load)
    }

    override fun onIntent(intent: WebSettingsIntent) {
        when (intent) {
            WebSettingsIntent.Load -> load()
            is WebSettingsIntent.SelectLanguage -> selectLanguage(intent.tag)
            is WebSettingsIntent.UpdateDeleteConfirmationInput ->
                setState { copy(deleteConfirmationInput = intent.value) }
            WebSettingsIntent.OpenDeleteDialog -> setState { copy(showDeleteConfirmDialog = true, deleteError = null) }
            WebSettingsIntent.DismissDeleteDialog -> setState { copy(showDeleteConfirmDialog = false) }
            WebSettingsIntent.ConfirmDelete -> confirmDelete()
        }
    }

    private fun load() {
        scope.launch {
            setState { copy(isLoading = true) }

            getProfile().onSuccess { profile -> setState { copy(language = profile.language) } }
            getMyEnterprise().onSuccess { enterprise -> setState { copy(organizationName = enterprise.name) } }

            // Best-effort, mirrors ProfileScreen's subscriptionLabel load: a failure here must
            // never block the rest of the page (profile/language/deletion still work offline).
            val plans = getSubscriptionPlans().getOrNull().orEmpty()
            getMySubscription().onSuccess { sub ->
                val planInfo = plans.firstOrNull { it.plan == sub.plan }
                setState {
                    copy(
                        subscriptionPlan = sub.plan,
                        subscriptionPrice = planInfo?.price,
                        subscriptionCurrency = planInfo?.currency,
                        subscriptionBillingPeriod = planInfo?.billingPeriod,
                        subscriptionExpiresAt = sub.expiresAt,
                    )
                }
            }

            setState { copy(isLoading = false) }
        }
    }

    private fun selectLanguage(tag: String) {
        if (currentState.isLanguageSaving) return
        // Guard set synchronously, before scope.launch (dispatches asynchronously) — closes the
        // double-tap window that let two concurrent language updates fire (same pattern as
        // DispatchViewModel.dispatch()).
        setState { copy(isLanguageSaving = true, languageError = null) }
        scope.launch {
            updateLanguage(tag).fold(
                onSuccess = { setState { copy(isLanguageSaving = false, language = tag) } },
                onFailure = { e ->
                    val code = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLanguageSaving = false, languageError = code) }
                },
            )
        }
    }

    private fun confirmDelete() {
        if (currentState.isDeleting) return
        // Guard set synchronously, before scope.launch (dispatches asynchronously) — closes the
        // double-tap/double-click window that let two concurrent DELETE /enterprises/me calls fire
        // (same pattern as DispatchViewModel.dispatch()). Critical here: this is an irreversible,
        // cascading deletion of the whole Organisation.
        setState { copy(isDeleting = true, deleteError = null) }
        scope.launch {
            deleteEnterprise().fold(
                onSuccess = {
                    setState { copy(isDeleting = false, showDeleteConfirmDialog = false) }
                    _navEvent.send(WebSettingsNavEvent.OrganizationDeleted)
                },
                onFailure = { e ->
                    val code = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isDeleting = false, deleteError = code) }
                },
            )
        }
    }
}
